/**
 * useActiveInstanceForRecord — wykrywa aktywną instancję procesu dla rekordu (F17 T17.1.7).
 *
 * Wspólny hook używany przez komponenty embed na karcie rekordu
 * (`ApprovalActionsPanel`, `CancelInstanceButton`, `ProcessApprovalBanner`).
 *
 * Dwustopniowo:
 *   1. `GET /api/processes/instances?moduleCode=&recordId=` → lista instancji rekordu
 *      (sprawy rekordu — jedna strona odpowiedzi, domyślnie 50; firma rekordu służy bramce praw,
 *      nie filtrowi wierszy) ORAZ prawa
 *      do akcji obiegu w firmie rekordu
 *      (`recordPermissions`, decyzja właściciela 2026-10-08). „Żywa" instancja = `active`
 *      lub `suspended` (patrz `isLiveInstanceStatus`) — sprawa, na której można jeszcze działać;
 *      nowego startu pilnuje serwer (każda nieanulowana sprawa → `RECORD_HAS_INSTANCE`).
 *   2. Jeśli jest żywa → `GET /api/processes/instances/:id` → pełny detal
 *      (kroki + decyzje + zamrożony snapshot węzłów).
 *
 * Klucze query współdzielone (`recordInstancesKey` / `instanceDetailKey`) — banner
 * używa tych samych kluczy, więc react-query deduplikuje requesty. W pamięci pod
 * `recordInstancesKey` leży CAŁA odpowiedź (`RecordInstancesResult`: wiersze + prawa); każdy
 * obserwator tego klucza, któremu wystarczają wiersze, bierze je przez `select:
 * selectRecordInstanceRows`, a prawa — przez `useRecordProcessPermissions`.
 */

import type { RecordProcessPermissions } from '@saas/shared';
import { type QueryClient, useQuery } from '@tanstack/react-query';

import {
  isTargetModuleInactiveError,
  isTargetRecordDeletedError,
} from '@/lib/processes/approvals-api';
import {
  fetchInstanceDetail,
  fetchInstancesForRecord,
  type InstanceDetail,
  type InstanceStatus,
  isInstanceNotFoundError,
  type RecordInstanceListingRow,
  type RecordInstancesResult,
} from '@/lib/processes/instance-api';
import { DEFAULT_QUERY_RETRY } from '@/lib/query';

export const recordInstancesKey = (moduleCode: string, recordId: string) =>
  ['processes', 'instances', 'for-record', moduleCode, recordId] as const;

export const instanceDetailKey = (instanceId: string) =>
  ['processes', 'instances', 'detail', instanceId] as const;

/**
 * Reguła `retry` zapytań o szczegóły sprawy (`instanceDetailKey`) — JEDNA dla karty rekordu (baner,
 * historia, ten hak) i okna z listy decyzji (`PendingDecisionDialog`). Stany końcowe — rekord
 * sprawy usunięty (404 `TARGET_RECORD_DELETED`), moduł jego kartoteki nieaktywny (402
 * `TARGET_MODULE_INACTIVE`) i sprawa niedostępna dla pytającego (404 `INSTANCE_NOT_FOUND`) — NIE są
 * ponawiane: ponowienie dostanie tę samą odpowiedź, a domyślne ponowienie klienta opóźniałoby
 * komunikat (albo stan „brak sprawy") o około sekundę. Inne błędy — jak dotąd (`DEFAULT_QUERY_RETRY`
 * ponowień). Własna funkcja `retry` zastępuje domyślną klienta w całości, stąd liczba ponowień stoi
 * tu jawnie.
 */
export function retryInstanceDetailQuery(failureCount: number, error: unknown): boolean {
  const terminal =
    isTargetRecordDeletedError(error) ||
    isTargetModuleInactiveError(error) ||
    isInstanceNotFoundError(error);
  return !terminal && failureCount < DEFAULT_QUERY_RETRY;
}

/**
 * Czy szczegóły sprawy są dla pytającego niedostępne: ostatnie pobranie skończyło się 404
 * `INSTANCE_NOT_FOUND`. Pytający bez prawa „Podgląd spraw" dostaje od serwera listę spraw rekordu
 * zawężoną do tych, w których może TERAZ decydować — po jego głosie szczegóły wracają 404, zanim
 * lista się odświeży. To brak sprawy do pokazania (lista odświeża się sama po decyzji), nie awaria:
 * wołający nie pokazuje komunikatu błędu ANI starych danych z pamięci (po nieudanym odświeżeniu
 * zapytanie zachowuje poprzednie `data`). Przyjmuje wynik `useQuery` / element `useQueries`.
 */
export function isInstanceDetailGone(query: {
  readonly isError: boolean;
  readonly error: unknown;
}): boolean {
  return query.isError && isInstanceNotFoundError(query.error);
}

/**
 * Unieważnia listing spraw rekordu: wiersze ORAZ flagi praw obiegu (`recordPermissions`). Woła się
 * po udanym zapisie karty rekordu — serwer liczy flagi z firmy rekordu i jego kategorii / typu
 * dokumentu (decyzja właściciela 2026-10-08), a zapis mógł któryś z nich zmienić. Bez tego „Wyślij
 * do akceptacji" i „Anuluj proces" pokazywałyby stan sprzed zapisu do końca okresu świeżości danych
 * (minuta). Odświeżenie leci w tle: kto chce na nie poczekać, bierze zwróconą obietnicę.
 */
export function invalidateRecordInstances(
  queryClient: QueryClient,
  moduleCode: string,
  recordId: string,
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: recordInstancesKey(moduleCode, recordId) });
}

/**
 * `select` dla obserwatorów `recordInstancesKey`, którym wystarczają same wiersze (baner, historia,
 * pola procesu na formularzach faktur, wskaźnik autostartu). Stała referencja — react-query nie
 * przelicza projekcji bez zmiany danych.
 */
export const selectRecordInstanceRows = (
  result: RecordInstancesResult,
): RecordInstanceListingRow[] => result.rows;

/**
 * Statusy „żywe" — sprawa wciąż trwa (w toku): `active` (normalny przebieg) ORAZ `suspended`
 * (zawieszona, ale nieterminalna). To NIE jest „blokuje nowy start": start blokuje każda sprawa
 * rekordu inna niż anulowana — także zakończona (`completed`) i z błędem (`error`); unikalny indeks
 * bazy chroni tylko przed dwiema sprawami `active`, resztę odrzuca serwer (`RECORD_HAS_INSTANCE`),
 * a baner pokazuje „Wyślij" tylko wtedy, gdy rekord nie ma żadnej nieanulowanej sprawy.
 * Wołający: baner i ten hook — do wskazania sprawy, na której można jeszcze działać (decyzje,
 * anulowanie); `useRecordProcessAutostart` — jako sygnał, że uruchomiona automatycznie sprawa już
 * się pojawiła.
 */
export function isLiveInstanceStatus(status: InstanceStatus): boolean {
  return status === 'active' || status === 'suspended';
}

export interface RecordProcessSlots {
  /** Sprawa w toku (pierwsza `active`/`suspended` z listy) albo `null`. */
  live: RecordInstanceListingRow | null;
  /**
   * Sprawa „właściwa" dla akcji i steppera: żywa, inaczej najnowsza (po numerze) nieanulowana;
   * `null`, gdy nie ma żadnej nieanulowanej.
   */
  relevant: RecordInstanceListingRow | null;
  /** Slot wolny = rekord nie ma żadnej sprawy poza anulowanymi (wtedy wolno „Wyślij do akceptacji"). */
  free: boolean;
}

/**
 * Model „slotu" rekordu (jedna zajmująca sprawa naraz) — JEDNA reguła dla banera nad zakładkami
 * i dla przycisków decyzji w elemencie „Proces akceptacji” (historia). Liczona z PEŁNEJ listy spraw
 * rekordu: filtr wyświetlania (np. ukrycie anulowanych) nie może zmieniać, która sprawa jest żywa.
 */
export function resolveRecordProcessSlots(
  rows: readonly RecordInstanceListingRow[],
): RecordProcessSlots {
  const live = rows.find((row) => isLiveInstanceStatus(row.status)) ?? null;
  const nonCancelled = rows.filter((row) => row.status !== 'cancelled');
  const free = nonCancelled.length === 0;
  const relevant =
    live ??
    (nonCancelled.length > 0
      ? [...nonCancelled].sort((a, b) => b.instanceNumber - a.instanceNumber)[0]!
      : null);
  return { live, relevant, free };
}

const selectRecordProcessPermissions = (
  result: RecordInstancesResult,
): RecordProcessPermissions | null => result.recordPermissions;

/**
 * Prawa do akcji obiegu (start / anulowanie) w firmie REKORDU — z odpowiedzi listingu instancji,
 * bez własnego żądania (ten sam klucz zapytania co baner). `null` = nie wiemy: brak `recordId`,
 * odpowiedź jeszcze nie przyszła, OSTATNIE pobranie padło (także nieudane odświeżenie — ścisłe,
 * zgodne z banerem, który przy błędzie listy pokazuje sam komunikat błędu bez przycisków; stare
 * flagi nie są wtedy „ostatnimi znanymi") albo serwer nie podał flag (starsza wersja API).
 * Wołający traktuje `null` jak brak prawa (przycisk ukryty), a nie jak zgodę.
 */
export function useRecordProcessPermissions(
  moduleCode: string,
  recordId: string,
): RecordProcessPermissions | null {
  const query = useQuery({
    queryKey: recordInstancesKey(moduleCode, recordId),
    queryFn: () => fetchInstancesForRecord(moduleCode, recordId),
    enabled: !!moduleCode && !!recordId,
    select: selectRecordProcessPermissions,
  });
  return query.isError ? null : (query.data ?? null);
}

export interface UseActiveInstanceForRecordResult {
  /** Detal żywej (active/suspended) instancji rekordu lub `null` (brak żywej / jeszcze ładuje). */
  instance: InstanceDetail | null;
  /**
   * Prawa do akcji obiegu w firmie rekordu (patrz `useRecordProcessPermissions`); `null` = nie
   * wiemy (lista jeszcze się ładuje, ostatnie pobranie listy padło albo serwer nie podał flag).
   */
  recordPermissions: RecordProcessPermissions | null;
  /** Pierwszy fetch (lista lub detal aktywnej) trwa. */
  isLoading: boolean;
  /** Lista lub detal zwróciły błąd (404 `INSTANCE_NOT_FOUND` szczegółów to brak żywej sprawy, nie błąd). */
  isError: boolean;
}

export function useActiveInstanceForRecord(
  moduleCode: string,
  recordId: string,
): UseActiveInstanceForRecordResult {
  const listQuery = useQuery({
    queryKey: recordInstancesKey(moduleCode, recordId),
    queryFn: () => fetchInstancesForRecord(moduleCode, recordId),
    enabled: !!moduleCode && !!recordId,
  });

  const liveId = listQuery.data?.rows.find((row) => isLiveInstanceStatus(row.status))?.id ?? null;

  const detailQuery = useQuery({
    // Stały klucz-placeholder gdy brak żywej — query i tak `enabled:false`.
    queryKey: instanceDetailKey(liveId ?? '__none__'),
    queryFn: () => fetchInstanceDetail(liveId as string),
    enabled: liveId !== null,
    // Ta sama reguła ponowień co baner, historia i okno z listy (bez ponowień stanów końcowych).
    retry: retryInstanceDetailQuery,
  });

  // Disabled query w react-query v5 raportuje `isPending:true` — liczymy detalowe
  // ładowanie tylko gdy faktycznie mamy żywą instancję do pobrania.
  const detailLoading = liveId !== null && detailQuery.isPending;
  // 404 `INSTANCE_NOT_FOUND` (patrz `isInstanceDetailGone`): brak żywej sprawy, nie błąd i nie
  // stare dane z pamięci.
  const detailGone = liveId !== null && isInstanceDetailGone(detailQuery);

  return {
    instance: liveId !== null && !detailGone ? (detailQuery.data ?? null) : null,
    recordPermissions: listQuery.isError ? null : (listQuery.data?.recordPermissions ?? null),
    isLoading: listQuery.isPending || detailLoading,
    isError: listQuery.isError || (detailQuery.isError && !detailGone),
  };
}
